import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faPencilAlt,
    faTrash,
    faArrowRight,
    faCheckCircle,
    faTimesCircle,
    faSave
} from '@fortawesome/free-solid-svg-icons';
import DeleteAlert from '../Shared/DeleteAlert';
import ModalPopUp from '../Shared/ModalPopUp';
import Select from 'react-select';

const AdminPlanFeature = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { planId, planName } = location.state || {};

    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);

    // Dialogs
    const [deleteDialog, setDeleteDialog] = useState({ open: false, feature: null });
    const [editModal, setEditModal] = useState({ open: false, feature: null });
    const [addModal, setAddModal] = useState(false);

    const [form, setForm] = useState({ name: '', screen_name: '', included: true, plan_feature_id: '' });
    const [submitting, setSubmitting] = useState(false);
    const [featureCatalog, setFeatureCatalog] = useState([]);

    const fetchCatalog = async () => {
        try {
            const res = await ApiServices.get('Subscription/PlanFeture/getAll');
            if (res && res.data) setFeatureCatalog(res.data);
        } catch (e) { }
    };

    const fetchFeatures = async () => {
        if (!planId) return;
        setLoading(true);
        try {
            // Re-fetch plan to get its features
            const res = await ApiServices.get('Subscription/Plan/getAll');
            const currentPlan = res.data.find(p => p.id === planId);
            if (currentPlan) {
                setFeatures(currentPlan.features || []);
            }
        } catch (e) {
            toastHelper.error('فشل في تحميل المميزات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!planId) navigate('/admin/dashboard');
        fetchFeatures();
        fetchCatalog();
    }, [planId]);

    const handleDelete = async () => {
        try {
            setLoading(true);
            // Based on user code: Subscription/PlanPlanFeture/delete/{id}
            const response = await ApiServices.delete(`Subscription/PlanPlanFeture/delete/${deleteDialog.feature.id}`);
            if (response && response.check) {
                toastHelper.success('تم حذف الميزة بنجاح');
                fetchFeatures();
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء الحذف');
        } finally {
            setLoading(false);
            setDeleteDialog({ open: false, feature: null });
        }
    };

    const handleSaveFeature = async () => {
        if (!form.name.trim()) return toastHelper.error('يرجى إدخال اسم الميزة');

        setSubmitting(true);
        try {
            let response;
            if (editModal.open) {
                // Update feature: Subscription/PlanFeture/update/{id}
                response = await ApiServices.put(`Subscription/PlanFeture/update/${editModal.feature.plan_feature_id}`, {
                    name: form.name.trim(),
                    screen_name: form.screen_name.trim()
                });
            } else {
                // Add new feature to plan
                // The endpoint might be Subscription/PlanPlanFeture/create or via Plan update
                // Based on common patterns:
                response = await ApiServices.post('Subscription/PlanPlanFeture/create', {
                    plan_id: planId,
                    name: form.name.trim(),
                    screen_name: form.screen_name.trim(),
                    included: form.included
                });
            }

            if (response && response.check) {
                toastHelper.success(editModal.open ? 'تم تحديث الميزة' : 'تمت إضافة الميزة');
                fetchFeatures();
                setEditModal({ open: false, feature: null });
                setAddModal(false);
                setForm({ name: '', screen_name: '', included: true });
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء العملية');
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenEdit = (feature) => {
        setForm({
            name: feature.name,
            screen_name: feature.screen_name || '',
            included: feature.included
        });
        setEditModal({ open: true, feature });
    };

    const columns = [
        { key: 'name', label: 'الميزة', sortable: true },
        { key: 'screen_name', label: 'اسم الشاشة', render: (val) => val || '-' },
        {
            key: 'included',
            label: 'الحالة',
            render: (val) => (
                <div className={`status-badge ${val ? 'active' : 'inactive'}`}>
                    <FontAwesomeIcon icon={val ? faCheckCircle : faTimesCircle} />
                    <span>{val ? 'متضمنة' : 'غير متضمنة'}</span>
                </div>
            )
        }
    ];

    const actions = (row) => [
        {
            label: 'تعديل',
            icon: <FontAwesomeIcon icon={faPencilAlt} />,
            onClick: (row) => handleOpenEdit(row),
            className: 'edit-btn'
        },
        {
            label: 'حذف',
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: (row) => setDeleteDialog({ open: true, feature: row }),
            className: 'delete-btn'
        }
    ];

    return (
        <div className="features-mgmt-container">
            <div className="page-header">
                <div className="header-left">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowRight} />
                        <span>عودة</span>
                    </button>
                    <div className="header-text">
                        <h1>مميزات الخطة: {planName}</h1>
                        <p>إدارة وتخصيص المميزات المتوفرة لهذه الباقة</p>
                    </div>
                </div>
                <button className="add-btn" onClick={() => navigate('/admin/plans/features/add', { state: { planId, planName } })}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>إضافة ميزة</span>
                </button>
            </div>

            <SoftGrid
                columns={columns}
                data={features}
                isLoading={loading}
                actions={actions}
            />

            {/* Add/Edit Modal */}
            <ModalPopUp
                isOpen={addModal || editModal.open}
                onClose={() => { setAddModal(false); setEditModal({ open: false, feature: null }); }}
                title={editModal.open ? 'تعديل ميزة' : 'إضافة ميزة جديدة'}
            >
                <div className="feature-form">
                    <div className="input-group">
                        <label>اسم الميزة</label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="مثلاً: قوالب جاهزة"
                        />
                    </div>
                    <div className="input-group">
                        <label>اسم الشاشة (اختياري)</label>
                        <input
                            type="text"
                            value={form.screen_name}
                            onChange={(e) => setForm({ ...form, screen_name: e.target.value })}
                            placeholder="اسم المسار أو الشاشة البرمجي"
                        />
                    </div>
                    <div className="input-group checkbox-row">
                        <label className="toggle-label">
                            <span>هل الميزة متضمنة؟</span>
                            <input
                                type="checkbox"
                                checked={form.included}
                                onChange={(e) => setForm({ ...form, included: e.target.checked })}
                            />
                        </label>
                    </div>

                    <button className="save-btn" onClick={handleSaveFeature} disabled={submitting}>
                        <FontAwesomeIcon icon={faSave} spin={submitting} />
                        <span>{submitting ? 'جاري الحفظ...' : 'حفظ التغييرات'}</span>
                    </button>
                </div>
            </ModalPopUp>

            <DeleteAlert
                isOpen={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, feature: null })}
                onConfirm={handleDelete}
                itemName={`الميزة ${deleteDialog.feature?.name}`}
                loading={loading}
            />

            <style>{`
                .features-mgmt-container { padding: 30px; direction: rtl; background: #f8fafc; min-height: 100vh; }
                .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
                .header-left { display: flex; align-items: center; gap: 20px; }
                .back-btn { padding: 10px 20px; border: none; background: white; color: #64748b; border-radius: 12px; cursor: pointer; display: flex; align-items: center; gap: 10px; font-weight: 700; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
                .header-text h1 { font-size: 1.8rem; color: #1e293b; margin: 0; font-weight: 800; }
                .header-text p { margin: 5px 0 0; color: #64748b; }
                
                .add-btn { background: #6366f1; color: white; border: none; padding: 12px 24px; border-radius: 12px; display: flex; align-items: center; gap: 10px; font-weight: 700; cursor: pointer; box-shadow: 0 4px 6px rgba(99, 102, 241, 0.3); }

                .status-badge { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 0.85rem; padding: 6px 12px; border-radius: 8px; width: fit-content; }
                .status-badge.active { background: #e0f2fe; color: #0369a1; }
                .status-badge.inactive { background: #fee2e2; color: #ef4444; }

                .feature-form { display: flex; flex-direction: column; gap: 20px; padding: 10px; }
                .feature-form .input-group label { display: block; margin-bottom: 8px; font-weight: 700; color: #475569; }
                .feature-form input[type="text"] { width: 100%; padding: 12px; border: 2px solid #e2e8f0; border-radius: 10px;   font-family: "Zain", sans-serif; }
                
                .toggle-label { display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 12px; background: #f8fafc; border-radius: 10px; border: 2px solid #e2e8f0; cursor: pointer; font-weight: 700; }
                .toggle-label input { width: 20px; height: 20px; }

                .save-btn { background: #6366f1; color: white; border: none; padding: 14px; border-radius: 10px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 10px; }
            `}</style>
        </div>
    );
};

export default AdminPlanFeature;
